import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  ArrowLeft, Weight, Ruler, Activity, Calendar, 
  FileText, Pill, TrendingUp, TrendingDown 
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [progressHistory, setProgressHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showProgressForm, setShowProgressForm] = useState(false);
  
  const [progressForm, setProgressForm] = useState({
    weight: '',
    height: '',
    notes: '',
    recommendations: '',
    meal_plan: '',
    prescribed_supplements: ''
  });

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  const fetchClientDetails = async () => {
    try {
      const [clientRes, progressRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/progress`)
      ]);
      setClient(clientRes.data);
      setProgressHistory(progressRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load client details');
      setLoading(false);
    }
  };

  const calculateBMI = (weight, height) => {
    if (weight && height) {
      const heightInMeters = height / 100;
      return (weight / (heightInMeters * heightInMeters)).toFixed(1);
    }
    return null;
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { label: 'Underweight', color: 'text-blue-600' };
    if (bmi < 25) return { label: 'Normal', color: 'text-green-600' };
    if (bmi < 30) return { label: 'Overweight', color: 'text-yellow-600' };
    return { label: 'Obese', color: 'text-red-600' };
  };

  const getHealthStatusBadge = (condition) => {
    const badges = {
      diabetes: 'bg-red-100 text-red-800',
      hypertension: 'bg-orange-100 text-orange-800',
      obesity: 'bg-yellow-100 text-yellow-800',
      gut_health: 'bg-purple-100 text-purple-800',
      sports_nutrition: 'bg-blue-100 text-blue-800',
      maternal_child: 'bg-pink-100 text-pink-800'
    };
    return badges[condition] || 'bg-gray-100 text-gray-800';
  };

  const handleProgressSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/clients/${id}/progress`, progressForm);
      toast.success('Progress recorded successfully');
      setShowProgressForm(false);
      fetchClientDetails();
      setProgressForm({
        weight: '',
        height: '',
        notes: '',
        recommendations: '',
        meal_plan: '',
        prescribed_supplements: ''
      });
    } catch (error) {
      toast.error('Failed to record progress');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-800"></div>
      </div>
    );
  }

  const bmiCategory = client?.bmi ? getBMICategory(client.bmi) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/clients')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">
              {client?.first_name} {client?.last_name}
            </h1>
            <p className="text-gray-600">Client ID: {client?.id?.substring(0, 8)}</p>
          </div>
        </div>
        <button
          onClick={() => setShowProgressForm(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          Record Progress
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Weight size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Current Weight</p>
              <p className="text-xl font-bold">{client?.weight || 'N/A'} kg</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Ruler size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Height</p>
              <p className="text-xl font-bold">{client?.height || 'N/A'} cm</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Activity size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">BMI</p>
              <p className="text-xl font-bold">
                {client?.bmi || 'N/A'}
                {bmiCategory && (
                  <span className={`text-sm ml-2 ${bmiCategory.color}`}>
                    ({bmiCategory.label})
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Visit</p>
              <p className="text-xl font-bold">
                {progressHistory[0] 
                  ? new Date(progressHistory[0].visit_date).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            {['overview', 'progress', 'meal-plans', 'prescriptions', 'appointments'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.replace('-', ' ')}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{client?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium">{client?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Gender</p>
                    <p className="font-medium capitalize">{client?.gender || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date of Birth</p>
                    <p className="font-medium">
                      {client?.date_of_birth 
                        ? new Date(client.date_of_birth).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Medical Conditions */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Medical Conditions</h3>
                <div className="flex flex-wrap gap-2">
                  {client?.medical_conditions?.map((condition, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 rounded-full text-sm ${getHealthStatusBadge(condition)}`}
                    >
                      {condition.replace('_', ' ')}
                    </span>
                  ))}
                  {(!client?.medical_conditions || client.medical_conditions.length === 0) && (
                    <p className="text-gray-500">No medical conditions recorded</p>
                  )}
                </div>
              </div>

              {/* Allergies */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Allergies</h3>
                <div className="flex flex-wrap gap-2">
                  {client?.allergies?.map((allergy, index) => (
                    <span key={index} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                      {allergy}
                    </span>
                  ))}
                  {(!client?.allergies || client.allergies.length === 0) && (
                    <p className="text-gray-500">No allergies recorded</p>
                  )}
                </div>
              </div>

              {/* Nutrition Goals */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Nutrition Goals</h3>
                <div className="flex flex-wrap gap-2">
                  {client?.nutrition_goals?.map((goal, index) => (
                    <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      {goal.replace('_', ' ')}
                    </span>
                  ))}
                  {(!client?.nutrition_goals || client.nutrition_goals.length === 0) && (
                    <p className="text-gray-500">No goals set</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="space-y-6">
              {/* Progress Chart */}
              {progressHistory.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-4">Weight Progress</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={progressHistory.slice().reverse()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="visit_date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                        formatter={(value) => [`${value} kg`, 'Weight']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="weight" 
                        stroke="#059669" 
                        strokeWidth={2}
                        dot={{ fill: '#059669' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Progress History */}
              <div className="space-y-4">
                <h4 className="font-semibold">Progress History</h4>
                {progressHistory.map((progress, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium">
                          Visit on {new Date(progress.visit_date).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600">
                          Recorded by: {progress.recorded_by_name}
                        </p>
                      </div>
                      <div className="flex space-x-4">
                        {progress.weight && (
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Weight</p>
                            <p className="font-bold">{progress.weight} kg</p>
                          </div>
                        )}
                        {progress.bmi && (
                          <div className="text-center">
                            <p className="text-sm text-gray-600">BMI</p>
                            <p className="font-bold">{progress.bmi}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {progress.notes && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Notes:</p>
                        <p className="text-sm text-gray-600">{progress.notes}</p>
                      </div>
                    )}
                    
                    {progress.recommendations && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Recommendations:</p>
                        <p className="text-sm text-gray-600">{progress.recommendations}</p>
                      </div>
                    )}
                    
                    {progress.prescribed_supplements?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Prescribed Supplements:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {progress.prescribed_supplements.map((supp, i) => (
                            <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {supp}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'meal-plans' && (
            <div className="text-center py-8">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Meal Plans</h3>
              <p className="text-gray-600">Meal plan management coming soon</p>
            </div>
          )}

          {activeTab === 'prescriptions' && (
            <div className="text-center py-8">
              <Pill size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Prescriptions</h3>
              <p className="text-gray-600">Supplement prescription tracking coming soon</p>
            </div>
          )}

          {activeTab === 'appointments' && (
            <div className="text-center py-8">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Appointments</h3>
              <p className="text-gray-600">Appointment history coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Progress Recording Modal */}
      {showProgressForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Record Progress</h2>
            <form onSubmit={handleProgressSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Weight (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={progressForm.weight}
                    onChange={(e) => setProgressForm({...progressForm, weight: e.target.value})}
                    className="w-full p-2 border rounded"
                    placeholder="Enter weight"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Height (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={progressForm.height}
                    onChange={(e) => setProgressForm({...progressForm, height: e.target.value})}
                    className="w-full p-2 border rounded"
                    placeholder="Enter height"
                  />
                </div>
              </div>

              {progressForm.weight && progressForm.height && (
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-sm">
                    Calculated BMI: <span className="font-bold">{calculateBMI(progressForm.weight, progressForm.height)}</span>
                    <span className="ml-2 text-xs">
                      ({getBMICategory(calculateBMI(progressForm.weight, progressForm.height)).label})
                    </span>
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={progressForm.notes}
                  onChange={(e) => setProgressForm({...progressForm, notes: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows="3"
                  placeholder="Enter consultation notes..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Recommendations</label>
                <textarea
                  value={progressForm.recommendations}
                  onChange={(e) => setProgressForm({...progressForm, recommendations: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows="3"
                  placeholder="Enter recommendations..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Meal Plan</label>
                <textarea
                  value={progressForm.meal_plan}
                  onChange={(e) => setProgressForm({...progressForm, meal_plan: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows="4"
                  placeholder="Enter meal plan details..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Prescribed Supplements</label>
                <input
                  type="text"
                  value={progressForm.prescribed_supplements}
                  onChange={(e) => setProgressForm({...progressForm, prescribed_supplements: e.target.value})}
                  className="w-full p-2 border rounded"
                  placeholder="Enter supplements (comma-separated)"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowProgressForm(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Save Progress
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetails;